import {
  FloatingFormItem,
  InputDecimal,
  InputText,
} from '@/components/_common';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Form, Card, Row, Col, Input, FormInstance, Select } from 'antd';
import { useEffect } from 'react';
import {
  InvoicePartFormValues,
  ufConsumptionOptions,
} from '../InvoicePartForm.types';
import { useInvoiceFormContext } from '../../../InvoiceFormProvider';

interface Props {
  form: FormInstance<InvoicePartFormValues>;
}

const InvoicePartFuelForm: React.FC<Props> = ({ form }) => {
  const ruleRequired = useDefaultEmptyFieldError();

  const { disabledFields } = useInvoiceFormContext();

  const formValues = Form.useWatch([], form);

  const hasCideValues =
    !!formValues?.fuel?.cideValue ||
    !!formValues?.fuel?.cideTaxRate ||
    !!formValues?.fuel?.cideCalculationBase;

  const hasClosingValues =
    !!formValues?.fuel?.tankNumber ||
    !!formValues?.fuel?.nozzleNumber ||
    !!formValues?.fuel?.endValue ||
    !!formValues?.fuel?.startValue;

  useEffect(() => {
    if (!hasCideValues) {
      form.setFields([
        {
          name: ['fuel', 'cideValue'],
          errors: [],
        },
        {
          name: ['fuel', 'cideTaxRate'],
          errors: [],
        },
        {
          name: ['fuel', 'cideCalculationBase'],
          errors: [],
        },
      ]);
    }
  }, [hasCideValues]);

  useEffect(() => {
    if (!hasClosingValues) {
      form.setFields([
        {
          name: ['fuel', 'tankNumber'],
          errors: [],
        },
        {
          name: ['fuel', 'nozzleNumber'],
          errors: [],
        },
        {
          name: ['fuel', 'endValue'],
          errors: [],
        },
        {
          name: ['fuel', 'startValue'],
          errors: [],
        },
      ]);
    }
  }, [hasClosingValues]);

  return (
    <Form form={form} preserve={false} disabled={disabledFields}>
      <Card
        title="Dados do combustível"
        type="inner"
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[24, 16]}>
          <Col span={24}>
            <FloatingFormItem
              label="Descrição do produto"
              name={['fuel', 'anpDescription']}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem label="GLP (%)" name={['fuel', 'glpPercentage']}>
              <InputDecimal fractions={4} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Gás natural nacional (%)"
              name={['fuel', 'gnnPercentage']}
            >
              <InputDecimal fractions={4} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Gás natural importado (%)"
              name={['fuel', 'gniPercentage']}
            >
              <InputDecimal fractions={4} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Valor de partida"
              name={['fuel', 'startingValue']}
            >
              <InputDecimal fractions={2} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Código ANP"
              name={['fuel', 'anpCode']}
              rules={[
                { type: 'string', min: 9 },
                { type: 'string', max: 9 },
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Descrição ANP"
              name={['fuel', 'anpDescription']}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="CODIF"
              name={['fuel', 'authorizationCode']}
              rules={[
                { type: 'string', min: 1 },
                { type: 'string', max: 27 },
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Qtde. comb. faturada temp. ambiente"
              name={['fuel', 'ambientTemperatureBilling']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="UF consumo"
              name={['fuel', 'consumptionState']}
            >
              <Select options={ufConsumptionOptions} allowClear />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="CIDE" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Base de cálculo"
              name={['fuel', 'cideCalculationBase']}
              rules={hasCideValues ? [ruleRequired] : []}
            >
              <InputDecimal fractions={4} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Alíquota"
              name={['fuel', 'cideTaxRate']}
              rules={hasCideValues ? [ruleRequired] : []}
            >
              <InputDecimal fractions={4} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Valor"
              name={['fuel', 'cideValue']}
              rules={hasCideValues ? [ruleRequired] : []}
            >
              <InputDecimal fractions={2} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="Encerrante" type="inner">
        <Row gutter={[24, 16]}>
          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Valor início"
              name={['fuel', 'startValue']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputDecimal fractions={3} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Valor final"
              name={['fuel', 'endValue']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputDecimal fractions={3} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Núm. de identificação do bico"
              name={['fuel', 'nozzleNumber']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Núm. de identificação do tanque"
              name={['fuel', 'tankNumber']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Núm. de identificação da bomba"
              name={['fuel', 'pumpNumber']}
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>
    </Form>
  );
};

export default InvoicePartFuelForm;
